import { defineStore } from 'pinia'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

export type ApprovalStatus = 'pending' | 'approved' | 'revision'

export interface ResumeFile {
  id: number
  user: number
  user_email: string
  user_first_name: string
  user_last_name: string
  user_avatar: string | null
  file: string
  external_url: string
  name: string
  approval_status: ApprovalStatus
  approved_by: number | null
  approved_by_email: string | null
  approved_at: string | null
  reviewer_notes: string
  is_default: boolean
  created_at: string
  updated_at: string
}

export const useResumeFilesStore = defineStore('resumeFiles', {
  state: () => ({
    resumeFiles: [] as ResumeFile[],
    loading: false,
    uploading: false,
    error: null as string | null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
    },
  }),

  getters: {
    defaultResume: (state) => state.resumeFiles.find((r) => r.is_default) ?? null,
    approvedResumes: (state) => state.resumeFiles.filter((r) => r.approval_status === 'approved'),
    isPaginated: (state) => state.pagination.totalPages > 1,
  },

  actions: {
    async fetchResumeFiles(userId?: string | number) {
      this.loading = true
      this.error = null
      try {
        const params = userId ? { user_id: userId } : undefined
        const response = await api.get(API_ROUTES.RESUME_FILES_LIST, { params })
        this.resumeFiles = response.data.results ?? response.data
      } catch (err: any) {
        this.error = err.response?.data?.detail || 'Failed to load resume files.'
        throw err
      } finally {
        this.loading = false
      }
    },

    async fetchAllResumeFiles(params: { approval_status?: ApprovalStatus; search?: string; page?: number } = {}) {
      this.loading = true
      this.error = null
      try {
        const response = await api.get(API_ROUTES.RESUME_FILES_LIST, { params })
        const data = response.data
        if (data.results !== undefined) {
          this.resumeFiles = data.results
          this.pagination.totalCount = data.count ?? 0
          this.pagination.totalPages = Math.ceil((data.count ?? 0) / this.pagination.pageSize)
          this.pagination.currentPage = params.page ?? 1
        } else {
          this.resumeFiles = data
        }
      } catch (err: any) {
        this.error = err.response?.data?.detail || 'Failed to load resume files.'
        throw err
      } finally {
        this.loading = false
      }
    },

    async uploadResumeFile(file: File, name: string, userId?: string | number) {
      this.uploading = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('file', file)
        if (name.trim()) formData.append('name', name.trim())
        // Privileged users upload on behalf of the applicant they are viewing;
        // without this the backend attaches the file to the uploader.
        if (userId) formData.append('user_id', String(userId))

        const response = await api.post(API_ROUTES.RESUME_FILES_LIST, formData)
        this.resumeFiles.unshift(response.data)
        return response.data as ResumeFile
      } catch (err: any) {
        this.error = err.response?.data?.file?.[0]
          || err.response?.data?.non_field_errors?.[0]
          || err.response?.data?.detail
          || 'Failed to upload resume.'
        throw err
      } finally {
        this.uploading = false
      }
    },

    async deleteResumeFile(id: number) {
      try {
        await api.delete(API_ROUTES.RESUME_FILE_DETAIL(id))
        this.resumeFiles = this.resumeFiles.filter((r) => r.id !== id)
      } catch (err: any) {
        this.error = err.response?.data?.detail || 'Failed to delete resume.'
        throw err
      }
    },

    async setDefault(id: number) {
      try {
        const response = await api.post(API_ROUTES.RESUME_FILE_SET_DEFAULT(id))
        this._patchLocal(response.data)
        // Clear is_default on all others
        this.resumeFiles.forEach((r) => {
          if (r.id !== id) r.is_default = false
        })
      } catch (err: any) {
        this.error = err.response?.data?.error || 'Failed to set default.'
        throw err
      }
    },

    async unsetDefault(id: number) {
      try {
        const response = await api.post(API_ROUTES.RESUME_FILE_UNSET_DEFAULT(id))
        this._patchLocal(response.data)
      } catch (err: any) {
        this.error = err.response?.data?.error || 'Failed to unset default.'
        throw err
      }
    },

    async replaceFile(id: number, file: File) {
      this.uploading = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await api.post(API_ROUTES.RESUME_FILE_REPLACE(id), formData)
        this._patchLocal(response.data)
        return response.data as ResumeFile
      } catch (err: any) {
        this.error = err.response?.data?.error
          || err.response?.data?.file?.[0]
          || 'Failed to replace resume.'
        throw err
      } finally {
        this.uploading = false
      }
    },

    async approve(id: number) {
      try {
        const response = await api.post(API_ROUTES.RESUME_FILE_APPROVE(id))
        this._patchLocal(response.data)
      } catch (err: any) {
        this.error = err.response?.data?.error || 'Failed to approve resume.'
        throw err
      }
    },

    async requestRevision(id: number, reviewerNotes: string) {
      try {
        const response = await api.post(API_ROUTES.RESUME_FILE_REQUEST_REVISION(id), {
          reviewer_notes: reviewerNotes,
        })
        this._patchLocal(response.data)
      } catch (err: any) {
        this.error = err.response?.data?.error || 'Failed to request revision.'
        throw err
      }
    },

    _patchLocal(updated: ResumeFile) {
      const idx = this.resumeFiles.findIndex((r) => r.id === updated.id)
      if (idx !== -1) this.resumeFiles[idx] = updated
    },
  },
})
